import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
  Badge,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Breadcrumbs,
  Link,
  Chip
} from '@mui/material';
import { 
  Send as SendIcon, 
  Delete as DeleteIcon,
  Image as ImageIcon,
  Search as SearchIcon,
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  MoreVert as MoreVertIcon,
  Analytics as AnalyticsIcon,
  History as HistoryIcon,
  Star as StarIcon,
  AttachFile as AttachFileIcon,
  Help as HelpIcon,
  Store as StoreIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Download as DownloadIcon,
  Menu as MenuIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  Assessment as AssessmentIcon,
  Fullscreen as FullscreenIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { generateAIResponse } from '../services/openaiService';
import logo from '../assets/logo0.png';

const AIChat = () => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [attachmentType, setAttachmentType] = useState(null);
  const [attachmentMenu, setAttachmentMenu] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(4);
  const [currentFile, setCurrentFile] = useState("Human rights.pdf");
  const [zoomLevel, setZoomLevel] = useState(100);
  const [rotation, setRotation] = useState(0);

  const [conversations] = useState([
    {
      id: 1,
      lastMessage: "How can I implement machine learning in my project?",
      date: "24/02/2025",
      time: "14:30",
      messageCount: 12,
      saved: true
    },
    {
      id: 2,
      lastMessage: "What are the best practices for React performance?",
      date: "24/02/2025",
      time: "15:45",
      messageCount: 8,
      saved: false
    },
    {
      id: 3,
      lastMessage: "Explain the concept of blockchain technology...",
      date: "24/02/2025",
      time: "16:20",
      messageCount: 15,
      saved: true
    }
  ]);

  const handleMenuClick = (event, conversationId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedConversation(conversationId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedConversation(null);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage({
          url: e.target.result,
          name: file.name
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeSelectedImage = () => {
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() && !selectedImage) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      image: selectedImage
    };

    setInput('');
    setSelectedImage(null);
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    setError(null);

    try {
      const aiResponse = await generateAIResponse(userMessage.content);
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
    } catch (error) {
      console.error('Chat Error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError(null);
  };

  const handleAttachmentClick = (event) => {
    setAttachmentMenu(event.currentTarget);
  };

  const handleAttachmentClose = () => {
    setAttachmentMenu(null);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Handle different file types
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setSelectedImage({
            url: e.target.result,
            name: file.name,
            type: file.type
          });
        };
        reader.readAsDataURL(file);
      }
      // You can add more file type handlers here
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100vh',
      backgroundColor: '#ffffff'
    }}>
      {/* Top Navigation Bar - Now Black */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1,
        backgroundColor: '#000000',
        color: '#ffffff'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <img 
            src={logo} 
            alt="Larvarris" 
            style={{ 
              height: '32px',
              filter: 'brightness(0) invert(1)' // Makes logo white
            }} 
          />
          <TextField
            placeholder="Search"
            size="small"
            sx={{
              width: '300px',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#1a1a1a',
                borderRadius: 1,
                color: '#ffffff',
                '& fieldset': {
                  borderColor: '#333333'
                },
                '&:hover fieldset': {
                  borderColor: '#00a651'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00a651'
                }
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#666666'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#666666' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<ShareIcon />}
            sx={{
              color: '#ffffff',
              borderColor: '#333333',
              '&:hover': {
                borderColor: '#00a651',
                backgroundColor: 'rgba(0, 166, 81, 0.1)'
              }
            }}
          >
            Share
          </Button>
          <Button 
            variant="contained" 
            sx={{ 
              backgroundColor: '#00a651',
              '&:hover': {
                backgroundColor: '#008c44'
              }
            }}
          >
            Upgrade
          </Button>
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ 
        display: 'flex', 
        flexGrow: 1,
        overflow: 'hidden'
      }}>
        {/* Left Side - Chat */}
        <Box sx={{ 
          width: '50%', 
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid #e0e0e0'
        }}>
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <MenuIcon />
            <Typography variant="h6">
              Larvarris Beta AI Chat
              <Typography 
                component="span" 
                variant="caption" 
                sx={{ 
                  ml: 1,
                  color: '#00a651',
                  backgroundColor: 'rgba(0, 166, 81, 0.1)',
                  padding: '2px 6px',
                  borderRadius: 1
                }}
              >
                BETA
              </Typography>
            </Typography>
          </Box>

          {/* Chat Messages */}
          <Box sx={{ 
            flexGrow: 1, 
            overflowY: 'auto',
            p: 2,
            backgroundColor: '#ffffff'
          }}>
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  mb: 3
                }}
              >
                <Typography 
                  variant="body1" 
                  sx={{ 
                    backgroundColor: message.role === 'user' ? '#f5f5f5' : '#ffffff',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #e0e0e0'
                  }}
                >
                  {message.content}
                </Typography>
                {message.citations && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {message.citations.map((citation, idx) => (
                      <Chip
                        key={idx}
                        label={`[${citation}]`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          {/* Input Area with File Upload */}
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask..."
              variant="outlined"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                        accept="image/*,.pdf,.doc,.docx"
                      />
                      <IconButton 
                        onClick={() => fileInputRef.current.click()}
                        sx={{ mr: 1 }}
                      >
                        <AttachFileIcon />
                      </IconButton>
                      <IconButton onClick={handleSubmit}>
                        <SendIcon />
                      </IconButton>
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1
                }
              }}
            />
            {selectedImage && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={selectedImage.name}
                  onDelete={() => setSelectedImage(null)}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(0, 166, 81, 0.1)',
                    color: '#00a651',
                    '& .MuiChip-deleteIcon': {
                      color: '#00a651',
                      '&:hover': {
                        color: '#008c44'
                      }
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* Right Side - Document Viewer */}
        <Box sx={{ 
          width: '50%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Document Controls Bar */}
          <Box sx={{ 
            p: 1,
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f9fa'
          }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {/* Zoom Controls */}
              <Tooltip title="Zoom Out">
                <IconButton 
                  size="small"
                  onClick={() => setZoomLevel(prev => Math.max(50, prev - 10))}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography 
                variant="body2" 
                sx={{ 
                  minWidth: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {zoomLevel}%
              </Typography>
              <Tooltip title="Zoom In">
                <IconButton 
                  size="small"
                  onClick={() => setZoomLevel(prev => Math.min(200, prev + 10))}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              {/* Rotation Controls */}
              <Tooltip title="Rotate Left">
                <IconButton 
                  size="small"
                  onClick={() => setRotation(prev => prev - 90)}
                >
                  <RotateLeftIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rotate Right">
                <IconButton 
                  size="small"
                  onClick={() => setRotation(prev => prev + 90)}
                >
                  <RotateRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              {/* Additional Controls */}
              <Tooltip title="Fullscreen">
                <IconButton size="small">
                  <FullscreenIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print">
                <IconButton size="small">
                  <PrintIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Reports Button */}
            <Button
              variant="outlined"
              startIcon={<AssessmentIcon />}
              size="small"
              sx={{
                borderColor: '#00a651',
                color: '#00a651',
                '&:hover': {
                  borderColor: '#008c44',
                  backgroundColor: 'rgba(0, 166, 81, 0.1)'
                }
              }}
            >
              Reports
            </Button>
          </Box>

          {/* Document Content with zoom and rotation */}
          <Box sx={{ 
            flexGrow: 1, 
            overflowY: 'auto',
            p: 3,
            backgroundColor: '#f5f5f5'
          }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 4, 
                backgroundColor: '#ffffff',
                minHeight: '100%',
                transform: `scale(${zoomLevel/100}) rotate(${rotation}deg)`,
                transformOrigin: 'top left',
                transition: 'transform 0.2s ease'
              }}
            >
              {/* Document content */}
              <Typography variant="body1">
                Document content...
              </Typography>
            </Paper>
          </Box>

          {/* Document Navigation Footer - remains the same */}
          <Box sx={{ 
            p: 1,
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f9fa'
          }}>
            <Typography variant="body2">
              Page {currentPage} of {totalPages}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton size="small">
                <NavigateBeforeIcon />
              </IconButton>
              <IconButton size="small">
                <NavigateNextIcon />
              </IconButton>
              <IconButton size="small">
                <DownloadIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: '#ffffff',
            color: '#333',
            borderRadius: 0
          }
        }}
      >
        <MenuItem onClick={handleMenuClose}>
          <StarIcon sx={{ mr: 1, fontSize: 20 }} /> Save Chat
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <AnalyticsIcon sx={{ mr: 1, fontSize: 20 }} /> View Analytics
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <HistoryIcon sx={{ mr: 1, fontSize: 20 }} /> View History
        </MenuItem>
        <Divider sx={{ backgroundColor: '#e0e0e0' }} />
        <MenuItem onClick={handleMenuClose} sx={{ color: '#ff4444' }}>
          Delete Chat
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AIChat; 